import React, {useCallback, useEffect, useState} from 'react';
import _ from 'lodash';
import InfiniteScroll from 'react-infinite-scroll-component';

import {connect, resourceActions} from '../../redux/index.js';

import K from 'k';
import {Image, Checkbox, AddButton, SearchBar, Tag, Icon, Button, Popup, Upload} from '@henrybuilt/react-lib';
import {loadMedia} from 'helpers/loaders.js';
import {ModeOptions, RorschachTestDataSections} from './rorschach-page.js';
import {useOutletContext} from 'react-router-dom';
import lib from 'lib.js';
import {
  pointerWithin,
  DndContext,
  useSensor,
  useSensors,
  PointerSensor
} from '@dnd-kit/core';
import SortableImageGrid from 'components/drag-drop/sortable-image-grid.js';
import {arrayMove} from 'helpers/dnd-helpers.js';
import Loader from 'components/loader.js';
import useHotKey from 'hooks/useHotKey.js';

const GalleryBoard = ({
  mode, isHudVisible,
  //HINT redux store
  tags, tagCategories, media = [], trackMedia, updateProject
}) => {
  const [project, isLoading] = useOutletContext();
  const [filters, setFilters] = useState({});
  const [hasMore, setHasMore] = useState(true);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageCount, setPageCount] = useState(50);
  const [isMediaLoading, setIsMediaLoading] = useState(false);
  const [filteredMediaIds, setFilteredMediaIds] = useState([]);

  const [selectedMediaIds, setSelectedMediaIds] = useState([]);
  const [deselectedMediaIds, setDeselectedMediaIds] = useState([]);

  const [fullScreenMediaId, setFullScreenMediaId] = useState(null);

  const [draggingId, setDraggingId] = useState(null);

  const isClientMode = mode === ModeOptions.RORSCHACH;

  const projectId = project?.id;

  //HINT populate project.rorschachTestData if empty
  useEffect(() => {
    if (!_.isEmpty(project?.rorschachTestData) && !hasInitiallyLoaded) {
      //HINT selectedMediaIds is initially derived from project.rorschachTestData existing ids
      const existingMediaIds = _.flatMap(_.keys(project.rorschachTestData), sectionKey => _.map(project.rorschachTestData[sectionKey], 'id'));
      setHasInitiallyLoaded(true);
      setSelectedMediaIds(selectedMediaIds.concat(...existingMediaIds));
    }
    else if (mode === ModeOptions.RORSCHACH) {
      requestRorschachTestMedia();
    }
  }, [project?.rorschachTestData]);

  useEffect(() => {
    //HINT need to flush the deselectedMediaIds on mode switch
    if (_.includes([ModeOptions.RORSCHACHSELECTION, ModeOptions.PROPOSALSELECTION], mode)&& deselectedMediaIds.length > 0) {
      handleClearDeselected();
    }
    else if (mode === ModeOptions.RORSCHACH) {
      if (deselectedMediaIds.length > 0) {
        handleClearDeselected();
      }

      requestRorschachTestMedia();
    }

    if (fullScreenMediaId !== null) setFullScreenMediaId(null);
  }, [mode]);

  useEffect(() => {
    if (hasInitiallyLoaded) {
      updateRorschachTestData({mediaIdsToAdd: selectedMediaIds, mediaIdsToRemove: deselectedMediaIds});
    }
  }, [selectedMediaIds, deselectedMediaIds]);

  useEffect(() => {
    if (projectId) {
      setPageNumber(1);

      requestMedia({reset: true});
    }
  }, [filters, projectId, mode]);

  const updateRorschachTestData = useCallback(async ({mediaIdsToAdd, mediaIdsToRemove, updatedRanks, hitApi = true}) => {
    const updatedData = _.cloneDeep(project?.rorschachTestData);

    if (!_.isEmpty(project)) {
      const existingMediaIds = _.flatMap(_.keys(project.rorschachTestData), sectionKey => _.map(project.rorschachTestData[sectionKey], 'id'));

      if (mediaIdsToAdd?.length > 0) {
        //HINT insert valid mediaIdsToAdd into ORGANIZE section
        const mediaIdsToAddMinusExisting = _.difference(mediaIdsToAdd, existingMediaIds);

        const existingToOrganizeIds = _.get(updatedData, RorschachTestDataSections.ORGANIZE, []);

        //HINT newly added media data has the following properties: id, notes, and rank
        _.set(updatedData, RorschachTestDataSections.ORGANIZE, existingToOrganizeIds.concat(..._.map(mediaIdsToAddMinusExisting, id => ({id, notes: '', rank: existingMediaIds.length}))));
      }

      if (mediaIdsToRemove?.length > 0) {
        //HINT map over updatedData and remove invalid ids in mediaIdsToRemove
        const validMediaIdsToRemove = _.intersection(existingMediaIds, mediaIdsToRemove);

        if (validMediaIdsToRemove.length > 0) {
          _.forEach(_.keys(updatedData), sectionKey => {
            _.set(updatedData, sectionKey, _.filter(updatedData[sectionKey], ({id}) => !_.includes(validMediaIdsToRemove, id)));
          });
        }
      }

      if (!_.isEmpty(updatedRanks)) {
        _.forEach(_.keys(updatedData), sectionKey => {
          _.set(updatedData, sectionKey, _.map(updatedData[sectionKey], item => ({...item, rank: updatedRanks[item.id]})));
        });
      }

      await updateProject({id: project.id, hitApi, props: {rorschachTestData: updatedData}});
    }
  });

  const styles = {
    wrapper: {
      position: 'relative',
      display: 'grid',
      gridTemplateColumns: '1fr',
      gridTemplateRows: isClientMode ? `0 ${isHudVisible ? '50px' : '0'} 0 1fr` : 'repeat(2, 50px) auto 1fr',
      gridTemplateAreas:
        `"search"
         "actions"
         "filters"
         "image-grid"`,
      gap: '10px',
      gridArea: 'content',
      margin: '3rem 6rem 0 6rem',
      overflow: 'hidden',
    },

    // popup
    fullScreenImage: {
      maxHeight: '80vh',
      maxWidth: '70vw'
    },
    fullScreenActions: {
      position: 'fixed',
      bottom: '15px',
      left: '0px',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    fullScreenActionIcon: (flip = false) => ({
      width: 33 / 2,
      height: 33 / 2,
      marginLeft: !flip ? '2px' : 0,
      marginRight: flip ? '2px' : 0,
      marginTop: flip ? '2px' : 0,
      transform: flip ? 'rotate(180deg)' : '',
    }),
    fullScreenActionButton: {
      backgroundColor: K.colors.base,
      border: `2px solid ${K.colors.base}`,
      borderColor: K.colors.base,
      borderRadius: '100%',
      cursor: 'pointer',
      height: '33px',
      width: '33px',
      justifyContent: 'center',
      alignItems: 'center',
    },

    // search
    search: {
      gridArea: 'search',
      display: 'flex',
      alignItems: 'center',
    },
    addButton: {
      marginLeft: 11,
      position: 'relative',
      display: 'inline-flex',
      height: 'auto',
      width: 'auto',
    },
    searchWrapper: {
      display: 'flex',
      flexGrow: 1,
      marginRight: K.spacing,
    },
    searchInput: {
      border: '1px solid gray',
      borderRadius: K.borderRadius,
      paddingLeft: K.spacing,
    },

    // actions
    actions: {
      gridArea: 'actions',
      display: 'flex',
      alignItems: 'center',
    },
    action: {
      margin: `0 ${K.spacing}px 0 0`,
      cursor: 'pointer',
      justifyContent: 'center',
      alignItems: 'center',
    },
    actionButton: {
      borderRadius: K.borderRadius,
    },
    actionButtonContent: {
      margin: `0 ${K.spacing}px 0 ${K.spacing}px`,
    },

    // filters
    filters: {
      gridArea: 'filters',
      display: 'flex',
      alignItems: 'center',
      overflow: 'auto',
      padding: `${K.spacing}px 0`,
    },
    tag: (isLast) => ({
      ...(!isLast ? {marginRight: K.spacing / 2} : {}),
      borderRadius: K.borderRadius,
    }),

    // loader
    loaderWrapper: {
      gridArea: 'image-grid',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%'
    },

    // image grid
    gridContainer: {
      overflowY: 'auto',
      marginBottom: K.margin
    },
    gridContainerEndMessage: {
      gridColumn: '1 / -1',
      gridRowEnd: 'auto',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: K.spacing,
      marginBottom: 100,
      textAlign: 'center',
      borderTop: 'solid 1px gray',
      borderBottom: 'solid 1px gray',
    },
    imageGrid: {
      gridArea: 'image-grid',
      display: 'grid',
      gridGap: K.spacing,
      gridTemplateColumns: 'repeat(auto-fill, 250px)',
      gridAutoRows: '250px',
      gridAutoFlow: 'row',
      width: '100%',
    },
    clientImageGrid: {
      gridArea: 'image-grid',
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 28vw)',
      gridAutoRows: '28vw',
      gridGap: K.spacing * 5,
      gridAutoFlow: 'row',
      paddingBottom: '2.5vw',
    },
    image: (isDeselected) => ({
      ...(isClientMode && isDeselected ? {
        filter: 'grayscale(100%)',
        opacity: '80%',
      } : {}),
      width: isClientMode ? '28vw' : '250px',
      height: isClientMode ? '28vw' : '250px',
      borderRadius: K.borderRadius - 3,
      transition: 'opacity 0.3s ease-in, filter 0.3s ease-in',
    }),
    extraStyles: (isSelected) => ({
      topLeft: isSelected && mode !== ModeOptions.RORSCHACH ? {pointerEvents: 'auto', visibility: 'visible'} : {},
    }),
    fullScreenInactive: {
      backgroundColor: 'transparent',
      cursor: 'pointer',
      zIndex: 2
    },
    checkbox: {
      backgroundColor: 'transparent',
      border: 'none',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginLeft: 1,
      marginBottom: 1,
    },
    checkboxContainer: (isSelected) => ({
      backgroundColor: isSelected ? 'white' : 'transparent',
      border: `2px solid ${K.colors.base}`,
      borderColor: K.colors.base,
      borderRadius: 100,
      cursor: 'pointer',
      height: 33,
      width: 33,
      justifyContent: 'center',
      position: 'relative', // applied in order for zIndex to have effect otherrwise when position: static it doesn't have any effect
      zIndex: 2
    }),
  };

  const requestMedia = async ({reset} = {}) => {
    setIsMediaLoading(true);

    const resourceActions = {trackMedia};
    const where = {};

    if (mode === ModeOptions.RORSCHACH) {
      var mediaIds = _.flatMap(_.keys(project.rorschachTestData), sectionKey => {
        return _.map(project.rorschachTestData[sectionKey], 'id');
      });

      where.id = mediaIds;
    }

    if (_.includes(filters.company, 'vp')) where.vpCompatible = 1;
    if (_.includes(filters.company, 'hb')) where.hbCompatible = 1;

    if (_.includes([ModeOptions.RORSCHACHSELECTION, ModeOptions.RORSCHACH], mode)) where.inRorschachSet = 1;
    else if (mode === ModeOptions.PROPOSALSELECTION) where.inProposalSet = 1;

    const currentlyActiveTagIds = _.flatMap(_.filter(filters, (filter, filterKey) => {
      return !_.isNaN(_.toNumber(filterKey));
    }));

    if (currentlyActiveTagIds.length > 0) where.tagIds = currentlyActiveTagIds;
    const pageNum = currentlyActiveTagIds.length > 0 ? 1 : (reset ? 1 : pageNumber);

    const {media, hasMore} = await loadMedia({resourceActions, pageNumber: pageNum, pageCount, where, projectId, reset});

    if (currentlyActiveTagIds.length > 0) {
      setFilteredMediaIds(_.map(media, 'id'));
    }

    if (hasMore) setPageNumber((reset ? 1 : pageNumber) + 1);

    setHasMore(hasMore);
    setIsMediaLoading(false);
  };

  const requestRorschachTestMedia = async () => {
    setIsMediaLoading(true);

    const mediaIds = _.flatMap(_.keys(project?.rorschachTestData), sectionKey => {
      return _.map(project.rorschachTestData[sectionKey], 'id');
    });

    const resourceActions = {trackMedia};
    const where = {id: mediaIds};

    if (_.includes([ModeOptions.RORSCHACHSELECTION, ModeOptions.RORSCHACH], mode)) where.inRorschachSet = 1;
    else if (mode === ModeOptions.PROPOSALSELECTION) where.inProposalSet = 1;

    await loadMedia({resourceActions, where, pageCount: mediaIds.length, projectId});

    setIsMediaLoading(false);
  };

  // Uploading
  const handleBatchStart = async (args) => {
    let newMedia = [];

    setIsMediaLoading(true);

    await lib.async.forEach(args, async (fileData) => {
      const {id} = await lib.api.create('medium', {props: {type: 'image', inRorschachSet: 0, inProposalSet: 0}}, {files: [fileData.file]});

      if (project?.id) {
        await lib.api.create('edge', {props: {fromId: id, toId: project.id, fromResourceKey: 'media', toResourceKey: 'project', data: {isRorschachTest: true}}});
      }

      const newMedium = await lib.api.get('medium', {where: {id}});

      newMedia.push(newMedium);
    });

    if (!_.isEmpty(newMedia)) {
      const newMediaIds = _.map(newMedia, 'id');

      await updateRorschachTestData({mediaIdsToAdd: newMediaIds});

      setSelectedMediaIds(selectedMediaIds.concat(...newMediaIds));
    }

    await trackMedia({media: newMedia});

    setIsMediaLoading(false);
  };

  // Media selection
  const someMediaSelected = selectedMediaIds.length > 0;
  const areAllMediaSelected = someMediaSelected && selectedMediaIds.length === _.keys(media).length;

  const toggleAllSelected = () => {
    if (!areAllMediaSelected) {
      setSelectedMediaIds(_.map(media, 'id'));

      setDeselectedMediaIds([]);
    }
    else {
      setDeselectedMediaIds(_.map(media, 'id'));

      setSelectedMediaIds([]);
    }
  };

  const handleSelectMedia = (id) => () => {
    const updatedSelections = _.clone(selectedMediaIds);
    const indexOfId = _.indexOf(updatedSelections, id);

    if (indexOfId === -1) { //HINT add
      updatedSelections.push(id);

      setSelectedMediaIds(updatedSelections);

      //HINT remove id from selectedMediaIds
      const updatedDeselections = _.clone(deselectedMediaIds);
      const indexOfDeselectedId = _.indexOf(updatedDeselections, id);

      if (indexOfDeselectedId !== -1) { //HINT remove
        updatedDeselections.splice(indexOfDeselectedId, 1);

        setDeselectedMediaIds(updatedDeselections);
      }
    }
    else { //HINT remove
      const updatedDeselections = _.clone(deselectedMediaIds);

      if (!_.includes(updatedDeselections, id)) { //HINT add to deselections
        updatedDeselections.push(id);

        setDeselectedMediaIds(updatedDeselections);
      }

      //HINT remove id from selectedMediaIds
      const updatedSelections = _.clone(selectedMediaIds);

      updatedSelections.splice(indexOfId, 1);

      setSelectedMediaIds(updatedSelections);
    }
  };

  const handleDeselectMedia = (id) => () => {
    const updatedDeselections = _.clone(deselectedMediaIds);
    const indexOfDeselectedId = _.indexOf(updatedDeselections, id);

    if (indexOfDeselectedId === -1) { //HINT add to deselections
      updatedDeselections.push(id);

      //HINT remove id from selectedMediaIds
      const updatedSelections = _.clone(selectedMediaIds);
      const indexOfSelectedId = _.indexOf(updatedSelections, id);

      if (indexOfSelectedId !== -1) { //HINT remove from selections
        updatedSelections.splice(indexOfSelectedId, 1);

        setSelectedMediaIds(updatedSelections);
      }
    }
    else { //HINT add to selections and remove from deselections
      updatedDeselections.splice(indexOfDeselectedId, 1);

      const updatedSelections = _.clone(selectedMediaIds);

      if (!_.includes(updatedSelections, id)) {
        updatedSelections.push(id);

        setSelectedMediaIds(updatedSelections);
      }
    }

    setDeselectedMediaIds(updatedDeselections);
  };

  const handleClearDeselected = () => {
    setDeselectedMediaIds([]);
  };

  const handleCheckboxChange = (id) => !isClientMode ? handleSelectMedia(id) : handleDeselectMedia(id);

  // Filtering
  const filtersData = _.map([
    {key: 'company', label: 'Company', options: [{label: 'Henrybuilt', value: 'hb'}, {label: 'Space Theory', value: 'vp'}]},
    ..._.map(tagCategories, tagCategory => {
      return {
        key: `${tagCategory.id}`,
        label: tagCategory.title,
        options: _.map(_.filter(tags, {tagCategoryId: tagCategory.id}), tag => {
          return {label: tag.aliasesByContext.media, value: tag.id};
        })
      };
    })
  ], filterData => ({
    ...filterData,
    activeOptions: filters[filterData.key],
  }));

  const handleFilterChange = ({key, value}) => {
    let updatedFilters = _.cloneDeep(filters);

    if (updatedFilters[key]) {
      if (_.includes(updatedFilters[key], value)) {
        updatedFilters[key] = _.reject(updatedFilters[key], currentValue => currentValue === value);

        if (updatedFilters[key].length === 0) delete updatedFilters[key];
      }
      else {
        updatedFilters[key].push(value);
      }
    }
    else {
      updatedFilters[key] = [value];
    }

    setFilters(updatedFilters);
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  // Drag and drop + ordering
  const handleDragStart = (event) => {
    const {active} = event;

    setDraggingId(active.id);
  };

  const handleDragEnd = async ({over, active}) => {
    setDraggingId(null);

    if (!over) {
      return;
    }

    if (active.id !== over.id) {
      const fromIndex = active.data.current.sortable.index;
      const toIndex = over.data.current?.sortable.index || 0;

      const existingRanks = _.chain(project.rorschachTestData)
        .keys()
        .flatMap(sectionKey => project.rorschachTestData[sectionKey])
        .orderBy('rank')
        .filter(item => _.find(media, {id: item.id}))
        .value();

      const updatedRanks = {};

      _.forEach(arrayMove({array: existingRanks, fromIndex, toIndex}), ({id}, index) => {
        updatedRanks[id] = index;
      });

      if (!_.isEmpty(updatedRanks)) {
        await updateRorschachTestData({updatedRanks});
      }
    }
  };

  const shuffleMediaRanks = async () => {
    const projectSpecificIds = _.map(_.filter(media, 'isProjectSpecific'), 'id');
    const existingRanks = _.chain(_.keys(project.rorschachTestData))
      .flatMap(sectionKey => project.rorschachTestData[sectionKey])
      .filter(({id}) => !_.includes(projectSpecificIds, id))
      .orderBy('rank')
      .value();

    const updatedRanks = {};

    _.forEach(_.shuffle(existingRanks), ({id}, index) => {
      updatedRanks[id] = index;
    });

    //HINT ensure the project-specific media is sorted to the end of the list wehn shuffled
    _.forEach(projectSpecificIds, (id, index) => {
      updatedRanks[id] = _.max(_.values(updatedRanks)) + index + 1;
    });

    if (!_.isEmpty(updatedRanks)) {
      await updateRorschachTestData({updatedRanks});
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10
      }
    }),
  );

  // Fullscreen
  const handleOpenFullScreenMedia = (mediaId) => () => {
    setFullScreenMediaId(mediaId);
  };

  const handleCloseFullScreenMedia = () => {
    setFullScreenMediaId(null);
  };

  const handleCycleFullscreenMedia = (direction) => () => {
    if (fullScreenMediaId !== null) {
      const displayedMediaIds = _.map(getMediaToDisplay(), 'id');
      const currentIndex = _.indexOf(displayedMediaIds, fullScreenMediaId);
      const directionModifier = direction === 'left' ? -1 : 1;

      let nextIndex = 0;

      if (currentIndex === displayedMediaIds.length - 1 && direction === 'right') {
        nextIndex = 0;
      }
      else if (currentIndex === 0 && direction === 'left') {
        nextIndex = displayedMediaIds.length - 1;
      }
      else {
        nextIndex = currentIndex + directionModifier;
      }

      setFullScreenMediaId(displayedMediaIds[nextIndex]);
    }
  };

  useHotKey(handleCycleFullscreenMedia('left'), ['ArrowLeft'], [fullScreenMediaId]);
  useHotKey(handleCycleFullscreenMedia('right'), ['ArrowRight'], [fullScreenMediaId]);

  const isFullScreenPopupVisible = fullScreenMediaId !== null;
  const fullScreenMediaSrc = _.find(media, medium => medium.id === fullScreenMediaId)?.url;

  // Images
  const getMediaToDisplay = () => {
    if (!project?.rorschachTestData) {
      return [];
    }

    const currentlyActiveTagIds = _.flatMap(_.filter(filters, (filter, filterKey) => {
      return !_.isNaN(_.toNumber(filterKey));
    }));

    let mediaToDisplay = [];

    if (isClientMode) {
      mediaToDisplay = _.chain(project.rorschachTestData)
        .keys()
        .flatMap(sectionKey => project.rorschachTestData[sectionKey])
        .orderBy('rank')
        .filter(item => _.find(media, {id: item.id}))
        .value();
    }
    else {
      mediaToDisplay = _.chain(media)
        .filter(medium => currentlyActiveTagIds.length > 0 ? _.includes(filteredMediaIds, medium.id) : true)
        .orderBy(['isProjectSpecific', 'id'], ['asc', 'desc'])
        .value();
    }

    return mediaToDisplay;
  };

  const getExtras = (id) => {
    const isSelected = _.includes(selectedMediaIds, id);

    const topLeft = !isClientMode && (
      <Checkbox
        key={`image-checkbox-${id}`}
        alwaysRenderCheck
        checkTheme={isSelected ? 'black' : 'white'}
        containerStyle={styles.checkboxContainer(isSelected)}
        style={styles.checkbox}
        onChange={handleCheckboxChange(id)}
      />
    );

    const topRight = isClientMode && (
      <Button
        key={`image-close-${id}`}
        onClick={handleCheckboxChange(id)}
        style={styles.checkboxContainer(true)}
        contentStyle={styles.checkbox}
        icon={
          <Icon
            key={`image-close-${id}`}
            name="close"
          />
        }
      />
    );

    const bottomRight = (
      <Button
        onClick={handleOpenFullScreenMedia(id)}
        style={styles.checkboxContainer(true)}
        contentStyle={styles.checkbox}
        icon={
          <Icon
            key={`image-fullscreen-${id}`}
            name="fullscreenInactive"
            theme="black"
          />
        }
      />
    );

    return {
      topLeft,
      topRight,
      bottomRight,
    };
  };

  const getExtraStyles = (id) => {
    const isSelected = _.includes(selectedMediaIds, id);

    return styles.extraStyles(isSelected);
  };

  const getImageStyle = (id) => {
    const isDeselected = _.includes(deselectedMediaIds, id);

    return styles.image(isDeselected);
  };

  const mediaItems = getMediaToDisplay();

  return (
    <div style={styles.wrapper}>
      <Popup visible={isFullScreenPopupVisible} onClose={handleCloseFullScreenMedia}>
        <Image src={fullScreenMediaSrc} style={styles.fullScreenImage} />
        <div style={styles.fullScreenActions}>
          <div style={styles.action}>
            <Button style={styles.fullScreenActionButton} contentStyle={styles.actionButtonContent} onClick={handleCycleFullscreenMedia('left')}><Icon name={'rightArrow'} style={styles.fullScreenActionIcon(true)}/></Button>
          </div>
          <div style={styles.action}>
            <Button style={styles.fullScreenActionButton} contentStyle={styles.actionButtonContent} onClick={handleCycleFullscreenMedia('right')}><Icon name={'rightArrow'} style={styles.fullScreenActionIcon()}/></Button>
          </div>
        </div>
      </Popup>
      <div style={styles.search}>
        {!isClientMode && <>
          <SearchBar
            placeholder='Search...'
            wrapperStyle={styles.searchWrapper}
            style={styles.searchInput}
            filters={filtersData}
            onFilterChange={handleFilterChange}
          />
          {mode === ModeOptions.RORSCHACHSELECTION && (
            <Upload
              multiple
              wrapperStyle={styles.addButton}
              onBatchStart={handleBatchStart}
            ><AddButton /></Upload>
          )}
        </>}
      </div>
      <div style={styles.actions} className='content'>
        {isHudVisible && (!isClientMode
          ? (<>
            <div style={styles.action}>
              <Checkbox disabled={_.isEmpty(media)} checked={areAllMediaSelected} indeterminate={someMediaSelected} onChange={toggleAllSelected} label={'Select all'}/>
            </div>
            {!_.isEmpty(filters) && <div style={styles.action}>
              <Button disabled={_.isEmpty(filters)} style={styles.actionButton} contentStyle={styles.actionButtonContent} onClick={handleClearFilters}>Clear filters</Button>
            </div>}
          </>)
          : (
            <div style={styles.action}>
              <Button style={styles.actionButton} contentStyle={styles.actionButtonContent} onClick={shuffleMediaRanks}>Randomize</Button>
            </div>
          ))}
      </div>
      <div style={styles.filters}>
        {!_.isEmpty(filters) && !isClientMode && (
          _.map(filters, (filterData, filterKey) => {
            const filter = _.find(filtersData, {key: filterKey});

            return _.map(filterData, (activeFilterOption, index) => {
              return (
                <Tag
                  style={styles.tag(filterData.length - 1 === index)}
                  onClose={() => handleFilterChange({key: filter.key, value: activeFilterOption})}
                  closable={true}
                  key={`${filter.key}-${activeFilterOption}`}>
                  <span>{filter.label}: {_.find(filter.options, {value: activeFilterOption}).label}</span>
                </Tag>
              );
            });
          })
        )}
      </div>
      <div id={'MediaScrollContainer'} style={styles.gridContainer}>
        {isLoading ? (<div style={styles.loaderWrapper}><Loader /></div>)
          : isClientMode
            ? (
              <DndContext
                sensors={sensors}
                collisionDetection={pointerWithin}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                key={'dnd-image-context'}
              >
                <SortableImageGrid
                  {...{draggingId, getExtras, getExtraStyles, getImageStyle}}
                  id={'dnd-image-grid'}
                  key={'dnd-image-grid'}
                  items={mediaItems}
                  hasLoaded={!isMediaLoading}
                  gridStyle={styles.clientImageGrid}
                />
              </DndContext>
            )
            : (
              <InfiniteScroll
                hasMore={hasMore && !isClientMode}
                dataLength={_.keys(media).length}
                next={requestMedia}
                loader={
                  <div style={styles.gridContainerEndMessage}>
                    <Loader />
                  </div>
                }
                endMessage={
                  <div style={styles.gridContainerEndMessage}>
                    <b>{`End of Images - ${mediaItems.length} result${mediaItems.length === 1 ? '' : 's'}`}</b>
                  </div>
                }
                scrollableTarget={'MediaScrollContainer'}
                style={styles.imageGrid}
              >{_.map(mediaItems, ({url, id}) => {
                  const isDeselected = _.includes(deselectedMediaIds, id);
                  const isSelected = _.includes(selectedMediaIds, id);

                  return (
                    <Image
                      src={url}
                      key={`image-card-${id}`}
                      style={styles.image(isDeselected)}
                      extraStyles={styles.extraStyles(isSelected)}
                      extra={getExtras(id)}
                    />
                  );
                })}</InfiniteScroll>
            )}
      </div>
    </div>
  );
};

export default connect({
  mapState: state => {
    return {
      media: state.resources.media.byId,
      tagCategories: state.resources.tagCategories.byId,
      tags: state.resources.tags.byId,
    };
  },
  mapDispatch: {
    ..._.pick(resourceActions.projects, ['updateProject']),
    ..._.pick(resourceActions.media, ['trackMedia']),
  }
})(GalleryBoard);
